/**
 * BoardDashboardContainer
 *
 *
 */

import React, { useState, useEffect } from 'react';
import BoardDashboardPresenter from './BoardDashboardPresenter';
import MessageAlert from 'utils';
import { InquiryApi, StatisticsApi, ReviewApi } from 'api';
import dayjs from 'dayjs';
import moment from 'moment';

const DAY = -90;
const MOMENT_FORMAT = 'YYYY-MM-DD';
const startDate = moment().add(DAY, 'days').format(MOMENT_FORMAT);
const endDate = moment().format(MOMENT_FORMAT);
const startDateUnix = moment().add(DAY, 'days').startOf().unix();
const endDateUnix = moment().endOf().unix();

/**----------------------------------------
 * [Component] 총관리자: 장바구니 분석 - 컨테이너
 * ----------------------------------------
 */
const BoardDashboardContainer = ({ defaultShopId, permission }) => {
  /* ===== INIT ===== */
  /* ===== STATE ===== */
  const [currentShop, _setCurrentShop] = useState(defaultShopId ?? null);
  const [statisticsType, setStatisticsType] = useState('month');
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({
    product: null,
    category: null,
    shop: null,
    rawData: null,
  });
  const [updatedAt, setUpdatedAt] = useState([]);
  const [inquiryList, setInquiryList] = useState([]);
  const [reviewList, setReviewList] = useState([]);
  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  /**
   * 카트목록 조회
   * --
   */
  const handleGetCartList = async () => {
    setLoading(true);
    try {
      const dd =
        statisticsType === 'today'
          ? 1
          : statisticsType === 'week'
          ? -7
          : statisticsType === 'month'
          ? -30
          : -90;
      const { status, message, data } = await StatisticsApi.getStatistics(
        'cart',
        {
          startDate: dayjs().add(dd, 'days').startOf().format('YYYY-MM-DD'),
          endDate: dayjs().endOf().format('YYYY-MM-DD'),
          ...(defaultShopId && { shopId: currentShop }),
        }
      );
      if (status !== 200) throw { message };

      // Product ID별로 분류하여 저장
      const groupedByProductId = data.reduce((acc, item) => {
        if (!acc[item.product_id]) {
          acc[item.product_id] = {
            label: item.product_name,
            price: item.product_price,
            shop_information_name: item.shop_information_name,
            data: [],
          };
        }
        acc[item.product_id].data.push(item);
        return acc;
      }, {});

      // Category ID별로 분류하여 저장
      const groupedByCategoryId = data.reduce((acc, item) => {
        if (!acc[item.category_id]) {
          acc[item.category_id] = { label: item.category_name, data: [] };
        }
        acc[item.category_id].data.push(item);
        return acc;
      }, {});

      // Shop ID별로 분류하여 저장
      const groupedByShopId = data.reduce((acc, item) => {
        if (!acc[item.shop_information_id]) {
          acc[item.shop_information_id] = {
            label: item.shop_information_name,
            data: [],
          };
        }
        acc[item.shop_information_id].data.push(item);
        return acc;
      }, {});

      setTimeout(() => {
        setChartData({
          product: groupedByProductId,
          category: groupedByCategoryId,
          shop: groupedByShopId,
          rawData: data,
        });
        setLoading(false);
      }, 850);
    } catch (e) {
      setLoading(false);
      console.log('[BoardDashboardContainer][handleGetCartList] Error: ', e);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 문의목록 조회
   * --------------------
   */
  const handleGetInquiryList = async () => {
    setLoading(true);
    try {
      const { status, message, data } = await InquiryApi.getInquiries({
        ...(currentShop && { shop_information_id: currentShop }),
        // join_tables: `["user", "product"]`,
      });
      if (status !== 200) throw { message };

      setTimeout(() => {
        setInquiryList(
          data.filter(
            (item) =>
              item.inquiry_type !== 'FAQ' && item.inquiry_answer_id === null
          )
        );
        setLoading(false);
      }, 750);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 리뷰목록 조회
   * --------------------
   */
  const getReviewList = async () => {
    try {
      const { status, message, data } = await ReviewApi.getReviews({
        ...(currentShop && { shop_information_id: currentShop }),
        start_date: startDateUnix,
        end_date: endDateUnix,
      });
      if (status !== 200) throw { message };
      setReviewList(data.filter((item) => item.is_show === null));
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    handleGetCartList();
    handleGetInquiryList();
    getReviewList();
  }, []);
  useEffect(() => {
    handleGetCartList();
  }, [currentShop]);

  /* ===== RENDER ===== */
  const props = {
    loading,
    chartData,
    permission,
    statisticsType,
    inquiryList,
    reviewList,
    setStatisticsType,
    handleGetCartList,
  };
  // JSX: start
  return (
    <>
      <BoardDashboardPresenter {...props} />
    </>
  ); // JSX: end
};

BoardDashboardContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default BoardDashboardContainer;
